import { zodResolver } from '@hookform/resolvers/zod';
import {
  activityEntrySchema,
  adminUserSchema,
  okSchema,
  paginated,
  passwordSchema,
  emailSchema,
  usernameSchema,
  type AdminUser,
} from '@hearth/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  Ban,
  KeyRound,
  Loader2,
  ShieldOff,
  Trash2,
  VenetianMask,
  UserRound as UserRoundIcon,
} from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { ActivityTable } from '@/components/activity-table';
import { Confirm } from '@/components/confirm';
import { FormField } from '@/components/form-field';
import { PageHeader } from '@/components/page-header';
import { Pagination } from '@/components/pagination';
import { QueryState } from '@/components/query-state';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiRequest } from '@/lib/api';
import { applyServerErrors, errorMessage } from '@/lib/forms';
import { dateTime } from '@/lib/format';
import { switchIdentity, useAuthenticatedSession } from '@/lib/session';

export const Route = createFileRoute('/_app/admin/users/$userId')({
  validateSearch: (s: Record<string, unknown>): { page?: number } => ({
    page: Number(s.page) > 1 ? Number(s.page) : undefined,
  }),
  component: UserPage,
});

function UserPage() {
  const { userId } = Route.useParams();
  const user = useQuery({
    queryKey: ['admin', 'users', 'detail', userId],
    queryFn: ({ signal }) =>
      apiRequest(`/api/application/users/${userId}`, { schema: adminUserSchema, signal }),
  });
  return (
    <>
      <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2">
        <Link to="/admin/users" search={{}}>
          <ArrowLeft /> Users
        </Link>
      </Button>
      <QueryState query={user}>
        {(u) => (
          <>
            <PageHeader
              title={u.username}
              icon={UserRoundIcon}
              description={
                <span className="flex flex-wrap items-center gap-2">
                  {u.email}
                  {u.isAdmin && <Badge>Admin</Badge>}
                  {u.suspended && (
                    <Badge variant="destructive">
                      Suspended {u.suspendedAt && dateTime(u.suspendedAt)}
                    </Badge>
                  )}
                </span>
              }
            />
            <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
              <EditUserCard user={u} />
              <ActionsCard user={u} />
            </div>
            <UserActivity uuid={u.uuid} />
          </>
        )}
      </QueryState>
    </>
  );
}

const editSchema = z.object({
  email: emailSchema,
  username: usernameSchema,
  password: z.union([z.literal(''), passwordSchema]),
  isAdmin: z.boolean(),
  emailVerified: z.boolean(),
});
type EditValues = z.input<typeof editSchema>;

function useInvalidateUser(id: number) {
  const queryClient = useQueryClient();
  return (updated?: AdminUser) => {
    if (updated) queryClient.setQueryData(['admin', 'users', 'detail', String(id)], updated);
    return queryClient.invalidateQueries({ queryKey: ['admin'] });
  };
}

function EditUserCard({ user }: { user: AdminUser }) {
  const { user: me } = useAuthenticatedSession();
  const refresh = useInvalidateUser(user.id);
  const form = useForm<EditValues>({
    resolver: zodResolver(editSchema),
    values: {
      email: user.email,
      username: user.username,
      password: '',
      isAdmin: user.isAdmin,
      emailVerified: user.emailVerified,
    },
  });
  const save = useMutation({
    mutationFn: (v: EditValues) => {
      const dirty = form.formState.dirtyFields;
      const body: Record<string, unknown> = {};
      for (const key of ['email', 'username', 'isAdmin', 'emailVerified'] as const)
        if (dirty[key]) body[key] = v[key];
      if (v.password) body.password = v.password;
      return apiRequest(`/api/application/users/${user.id}`, {
        method: 'PATCH',
        body,
        schema: adminUserSchema,
      });
    },
    onSuccess: async (updated, v) => {
      await refresh(updated);
      toast.success(v.password ? 'User updated. Their sessions were signed out.' : 'User updated.');
    },
    onError: (err) => applyServerErrors(err, form.setError),
  });
  const isSelf = me.uuid === user.uuid;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Details</CardTitle>
        <CardDescription>Created {dateTime(user.createdAt)}</CardDescription>
      </CardHeader>
      <form onSubmit={form.handleSubmit((v) => save.mutate(v))} noValidate>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <FormField label="Email" error={form.formState.errors.email?.message}>
            <Input type="email" {...form.register('email')} />
          </FormField>
          <FormField label="Username" error={form.formState.errors.username?.message}>
            <Input {...form.register('username')} />
          </FormField>
          <div className="sm:col-span-2">
            <FormField
              label="New password"
              error={form.formState.errors.password?.message}
              description="Leave empty to keep the current password."
            >
              <Input type="password" autoComplete="new-password" {...form.register('password')} />
            </FormField>
          </div>
          {(
            [
              [
                'isAdmin',
                'Administrator',
                isSelf ? "You can't remove your own admin rights." : undefined,
              ],
              ['emailVerified', 'Email verified', undefined],
            ] as const
          ).map(([name, label, hint]) => (
            <Controller
              key={name}
              control={form.control}
              name={name}
              render={({ field }) => (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={name}
                    checked={field.value}
                    disabled={name === 'isAdmin' && isSelf}
                    onCheckedChange={(c) => field.onChange(c === true)}
                  />
                  <Label htmlFor={name}>{label}</Label>
                  {hint && <span className="text-muted-foreground text-xs">{hint}</span>}
                </div>
              )}
            />
          ))}
        </CardContent>
        <CardFooter className="mt-6 justify-end">
          <Button type="submit" disabled={save.isPending || !form.formState.isDirty}>
            {save.isPending && <Loader2 className="animate-spin" />} Save changes
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}

function ActionsCard({ user }: { user: AdminUser }) {
  const { user: me } = useAuthenticatedSession();
  const isSelf = me.uuid === user.uuid;
  const refresh = useInvalidateUser(user.id);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const call = (path: string, method: 'POST' | 'DELETE' | 'PATCH' = 'POST', body?: unknown) =>
    apiRequest(`/api/application/users/${user.id}${path}`, {
      method,
      body,
      schema: z.union([adminUserSchema, okSchema]),
    });

  const resetMail = useMutation({
    mutationFn: () => call('/password-reset'),
    onSuccess: () => toast.success(`Password reset link sent to ${user.email}.`),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const suspend = useMutation({
    mutationFn: () => call(user.suspended ? '/unsuspend' : '/suspend'),
    onSuccess: async (u) => {
      await refresh(u as AdminUser);
      toast.success(
        user.suspended ? 'User unsuspended.' : 'User suspended and signed out everywhere.',
      );
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const disable2fa = useMutation({
    mutationFn: () => call('', 'PATCH', { disableTwoFactor: true }),
    onSuccess: async (u) => {
      await refresh(u as AdminUser);
      toast.success('Two-factor authentication removed.');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: () => call('', 'DELETE'),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin'] });
      toast.success(`Deleted ${user.username}.`);
      await navigate({ to: '/admin/users', search: {} });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const impersonate = useMutation({
    mutationFn: () => call('/impersonate'),
    onSuccess: async () => {
      await switchIdentity(queryClient);
      await navigate({ to: '/' });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Actions</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-2">
        <Button
          variant="outline"
          className="justify-start"
          onClick={() => resetMail.mutate()}
          disabled={resetMail.isPending}
        >
          <KeyRound /> Send password reset email
        </Button>
        {user.twoFactorEnabled && (
          <Confirm
            title="Remove two-factor authentication?"
            description={`${user.username} will be able to sign in with just their password until they set it up again.`}
            action="Remove 2FA"
            onConfirm={() => disable2fa.mutate()}
          >
            <Button variant="outline" className="justify-start">
              <ShieldOff /> Remove two-factor
            </Button>
          </Confirm>
        )}
        <Button
          variant="outline"
          className="justify-start"
          disabled={isSelf || user.isAdmin || user.suspended || impersonate.isPending}
          title={user.isAdmin ? "Administrators can't be impersonated." : undefined}
          onClick={() => impersonate.mutate()}
        >
          <VenetianMask /> Sign in as {user.username}
        </Button>
        <Confirm
          title={user.suspended ? `Unsuspend ${user.username}?` : `Suspend ${user.username}?`}
          description={
            user.suspended
              ? 'They will be able to sign in again.'
              : 'They will be signed out everywhere and unable to sign in.'
          }
          action={user.suspended ? 'Unsuspend' : 'Suspend'}
          onConfirm={() => suspend.mutate()}
        >
          <Button variant="outline" className="justify-start" disabled={isSelf}>
            <Ban /> {user.suspended ? 'Unsuspend' : 'Suspend'}
          </Button>
        </Confirm>
        <Confirm
          title={`Delete ${user.username}?`}
          description="This permanently deletes the account. Activity records are kept without the link to this user."
          action="Delete user"
          destructive
          onConfirm={() => remove.mutate()}
        >
          <Button variant="outline" className="text-destructive justify-start" disabled={isSelf}>
            <Trash2 /> Delete user
          </Button>
        </Confirm>
      </CardContent>
    </Card>
  );
}

function UserActivity({ uuid }: { uuid: string }) {
  const { page = 1 } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const query = useQuery({
    queryKey: ['admin', 'activity', 'user', uuid, page],
    queryFn: ({ signal }) =>
      apiRequest(`/api/application/activity?user=${uuid}&page=${page}&perPage=20`, {
        schema: paginated(activityEntrySchema),
        signal,
      }),
    placeholderData: keepPreviousData,
  });
  return (
    <section className="mt-8 space-y-4">
      <h2 className="text-lg font-semibold">Activity</h2>
      <QueryState query={query}>
        {(data) => (
          <>
            <ActivityTable entries={data.data} />
            <Pagination
              {...data.meta}
              onPageChange={(p) => void navigate({ search: { page: p } })}
            />
          </>
        )}
      </QueryState>
    </section>
  );
}
